import axios from 'axios';
import { getAuth, getCurrentTeamToken, isRunningInIframe } from './identity.service';

const _getBaseHeaders = (contentType = 'application/json') => {
  const headers = {
    'content-type': contentType
  };
  try {
    const timezone = typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
    if (timezone) {
      headers['x-timezone'] = timezone;
    }
  } catch (e) {
    // ignore timezone resolution errors
  }
  return headers;
};

const _getAuthHeaders = (contentType = 'application/json') => {
  const auth = getAuth();
  const teamToken = getCurrentTeamToken();
  const headers = _getBaseHeaders(contentType);
  headers['x-access-token'] = auth?.token;
  headers['x-team-token'] = teamToken;
  if (isRunningInIframe && isRunningInIframe()) {
    headers['x-iframe-request'] = '1';
  }
  return headers;
};

const _handleResponse = (response, resolve, reject, rejectFalseResponse) => {
  if (response && response.data) {
    if (rejectFalseResponse && response.data?.status === false) {
      reject(response.data);
    }
    resolve(response.data);
  }
};

const _handleError = (error, reject) => {
  // if (error.status === 401) {
  //   removeAuth();
  //   removeCurrentTeamToken();
  // }
  reject(error);
};

export const postWithOutAuth = (url, entity, usernameHeader = null, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  const headers = _getBaseHeaders();
  if (usernameHeader) {
    headers['x-username'] = usernameHeader;
  }
  axios.post(url, entity, { headers }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    reject(ex);
  });
});

export const postWithAuth = (url, entity, contentType = 'application/json', rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.post(url, entity, { headers: _getAuthHeaders(contentType), timeout: 20_000, timeoutErrorMessage: 'Request timed out' }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    _handleError(ex, reject);
  });
});

export const getWithOutAuth = (url, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.get(url, { headers: _getBaseHeaders() }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    reject(ex);
  });
});

export const getWithAuth = (url, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.get(url, { headers: _getAuthHeaders() }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    _handleError(ex, reject);
  });
});

export const deleteWithOutAuth = (url, entity, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.delete(url, {
    headers: _getBaseHeaders(),
    data: entity
  }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    reject(ex);
  });
});

export const putWithAuth = (url, entity, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.put(url, entity, { headers: _getAuthHeaders() }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    _handleError(ex, reject);
  });
});

export const patchWithOutAuth = (url, entity, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.patch(url, entity, { headers: _getBaseHeaders() }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    reject(ex);
  });
});

export const patchWithAuth = (url, entity, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.patch(url, entity, { headers: _getAuthHeaders() }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    _handleError(ex, reject);
  });
});

export const deleteWithAuth = (url, entity, rejectFalseResponse = false) => new Promise((resolve, reject) => {
  axios.delete(url, { headers: _getAuthHeaders(), data: entity }).then((response) => {
    _handleResponse(response, resolve, reject, rejectFalseResponse);
  }).catch((ex) => {
    _handleError(ex, reject);
  });
});

export const getWithAuthServerSide = (url, auth, teamToken) => {
  const headers = _getBaseHeaders('application/json');
  headers['x-access-token'] = auth?.token;
  if (teamToken) {
    headers['x-team-token'] = teamToken;
  }
  return new Promise((resolve, reject) => {
    axios.get(url, { headers }).then((response) => {
      if (response && response.data) {
        resolve(response.data);
      }
    }).catch((ex) => {
      reject(ex);
    });
  });
};

export const getWithOutAuthServerSide = (url) => {
  const headers = _getBaseHeaders();
  return new Promise((resolve, reject) => {
    axios.get(url, { headers }).then((response) => {
      if (response && response.data) {
        resolve(response.data);
      }
    }).catch((ex) => {
      reject(ex);
    });
  });
};

export const postWithAuthServerSide = (url, auth, teamToken = null, entity = {}, usernameHeader) => {
  const headers = _getBaseHeaders('application/json');
  headers['x-access-token'] = auth?.token;
  if (teamToken) {
    headers['x-team-token'] = teamToken;
  }
  if (usernameHeader) {
    headers['x-username'] = usernameHeader;
  }
  return new Promise((resolve, reject) => {
    fetch(url, {
      headers,
      method: 'POST',
      body: JSON.stringify(entity)
    }).then(async (res) => {
      try {
        const response = await res.json();
        resolve(response);
      } catch (e) {
        reject(e);
      }
    }).catch((ex) => {
      reject(ex);
    });
  });
};

export const putWithAuthServerSide = (url, auth, teamToken = null, entity = {}) => {
  const headers = _getBaseHeaders('application/json');
  headers['x-access-token'] = auth?.token;
  if (teamToken) {
    headers['x-team-token'] = teamToken;
  }
  return new Promise((resolve, reject) => {
    fetch(url, {
      headers,
      method: 'PUT',
      body: JSON.stringify(entity)
    }).then(async (res) => {
      try {
        const response = await res.json();
        resolve(response);
      } catch (e) {
        reject(e);
      }
    }).catch((ex) => {
      reject(ex);
    });
  });
};
